// src/pages/Admin/area-notifications/AreaMap.tsx
//
// Mapbox-based circle/area visualizer for the admin area-broadcast feature.
// Shows a draggable (or fixed) pin at the broadcast center with a translucent
// purple circle representing the radius, and re-centers when the parent
// updates centerLat / centerLng (e.g. user picks a different market preset).
//
// Conventions reused from the rest of the admin site:
//   • Token: `MAPBOX_TOKEN` from `@/config/private/mapboxConfig`
//     (single source of truth that reads `REACT_APP_MAPBOX_TOKEN`).
//   • Map style: `mapbox://styles/mapbox/dark-v11` (matches Marketplace
//     market preview + the business modals).
//   • Imports come from `react-map-gl/mapbox` (v8 subpath).

import React, { useEffect, useMemo, useState } from 'react';
import Map, { Source, Layer, Marker, NavigationControl } from 'react-map-gl/mapbox';
import type { Feature, Polygon } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from '@/config/private/mapboxConfig';

// ─── Props ────────────────────────────────────────────────────────────────────

interface AreaMapProps {
  centerLat: number;
  centerLng: number;
  radiusMi: number;
  /** When true, the pin is draggable; when false, fixed at the market center. */
  draggable: boolean;
  onCenterChange?: (lat: number, lng: number) => void;
}

// ─── Circle math (inline, no shared util dep) ────────────────────────────────

function circlePolygon(
  lat: number,
  lng: number,
  radiusMi: number,
  points = 64,
): [number, number][] {
  const radiusKm = radiusMi * 1.609344;
  const earthRadiusKm = 6371;
  const coords: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dLat = (radiusKm / earthRadiusKm) * (180 / Math.PI);
    const dLng =
      ((radiusKm / earthRadiusKm) * (180 / Math.PI)) /
      Math.cos((lat * Math.PI) / 180);
    coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
  }
  coords.push(coords[0]);
  return coords;
}

// Pick a sensible default zoom from the radius so the circle roughly fills
// the map viewport. Tuned for a ~360px tall map at typical aspect ratios.
function zoomForRadius(radiusMi: number): number {
  if (radiusMi <= 1) return 13;
  if (radiusMi <= 3) return 12;
  if (radiusMi <= 7) return 11;
  if (radiusMi <= 15) return 10;
  if (radiusMi <= 30) return 9;
  return 8;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AreaMap(props: AreaMapProps): React.JSX.Element {
  const { centerLat, centerLng, radiusMi, draggable, onCenterChange } = props;

  // Controlled viewState so the map re-centers when centerLat/centerLng change
  // externally (e.g. user picks a different market preset). We let the user
  // pan/zoom locally via `onMove`, but reset on prop change.
  const [viewState, setViewState] = useState({
    longitude: centerLng,
    latitude: centerLat,
    zoom: zoomForRadius(radiusMi),
  });

  useEffect(() => {
    setViewState(v => ({
      ...v,
      longitude: centerLng,
      latitude: centerLat,
      zoom: zoomForRadius(radiusMi),
    }));
  }, [centerLat, centerLng, radiusMi]);

  // GeoJSON polygon approximating the radius circle.
  const circleFeature = useMemo<Feature<Polygon>>(() => {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [circlePolygon(centerLat, centerLng, radiusMi, 64)],
      },
    };
  }, [centerLat, centerLng, radiusMi]);

  // Missing-token fallback: render a yellow warning rather than letting the
  // Mapbox library throw at runtime. Placed AFTER hooks to satisfy
  // react-hooks/rules-of-hooks (hooks must run unconditionally on every render).
  if (!MAPBOX_TOKEN) {
    return (
      <div
        className="w-full rounded-xl border border-amber-700/50 bg-amber-950/40 text-amber-300 text-sm flex items-center justify-center px-4"
        style={{ height: 360 }}
      >
        Mapbox token missing — set <code className="mx-1 font-mono">REACT_APP_MAPBOX_TOKEN</code> to enable the map preview.
      </div>
    );
  }

  // Marker drag handler.
  // Note: react-map-gl's `MarkerDragEvent` type is awkward to import across
  // the v8 subpath exports, and the event shape is well-known and stable
  // (`{ lngLat: { lat, lng } }`). We type the minimal shape we read.
  const handleDragEnd = (evt: { lngLat: { lat: number; lng: number } }) => {
    if (!onCenterChange) return;
    onCenterChange(evt.lngLat.lat, evt.lngLat.lng);
  };

  return (
    <div
      className="w-full rounded-xl overflow-hidden border border-gray-700"
      style={{ height: 360 }}
    >
      <Map
        mapboxAccessToken={MAPBOX_TOKEN}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        style={{ width: '100%', height: '100%' }}
      >
        <NavigationControl position="top-right" />

        <Source id="area-circle" type="geojson" data={circleFeature}>
          <Layer
            id="area-circle-fill"
            type="fill"
            paint={{
              'fill-color': '#7c3aed',
              'fill-opacity': 0.18,
            }}
          />
          <Layer
            id="area-circle-line"
            type="line"
            paint={{
              'line-color': '#7c3aed',
              'line-width': 2,
            }}
          />
        </Source>

        <Marker
          latitude={centerLat}
          longitude={centerLng}
          color="#a78bfa"
          draggable={draggable}
          onDragEnd={draggable ? handleDragEnd : undefined}
        />
      </Map>
    </div>
  );
}

export default AreaMap;
